import React, { useState, useMemo } from 'react';
import { Modal } from "../../../components/ui/modal";
import { Users, Search, DollarSign, TrendingUp, BarChart, User, Truck, FileText, Calendar, ArrowUpDown, Filter } from 'lucide-react';
import useApi from "../../../hooks/useApi";
import Input from "../../../components/form/input/InputField";
import Button from "../../../components/ui/button/Button";
import { Gasto } from '../../../hooks/useApi';
import Badge from "../../../components/ui/badge/Badge";

type BadgeColor = "primary" | "success" | "error" | "warning" | "info" | "light" | "dark";

interface ProveedoresModalProps {
  isOpen: boolean;
  onClose: () => void;
  gastos: Gasto[];
}

interface ProveedorStats {
  name: string;
  totalSpent: number;
  transactions: number;
  lastTransaction: string;
  categories: Record<string, number>;
}

const ProveedoresModal: React.FC<ProveedoresModalProps> = ({ isOpen, onClose, gastos }) => {
  const { formatCurrency } = useApi();
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'amount'>('amount');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Calculate provider statistics
  const providersStats: ProveedorStats[] = useMemo(() => {
    // Group gastos by provider
    const providerMap = new Map<string, ProveedorStats>();
    
    gastos.forEach(gasto => {
      const providerName = gasto.proveedor;
      if (!providerName) return; // Skip entries without provider
      
      if (!providerMap.has(providerName)) {
        providerMap.set(providerName, {
          name: providerName,
          totalSpent: 0,
          transactions: 0,
          lastTransaction: '',
          categories: {}
        });
      }
      
      const stats = providerMap.get(providerName)!;
      stats.totalSpent += gasto.monto;
      stats.transactions += 1;
      
      // Update last transaction date if newer
      const gastoDate = new Date(gasto.fecha);
      const currentLastDate = stats.lastTransaction ? new Date(stats.lastTransaction) : new Date(0);
      if (gastoDate > currentLastDate) {
        stats.lastTransaction = gasto.fecha;
      }
      
      // Increment category counter
      if (!stats.categories[gasto.categoria]) {
        stats.categories[gasto.categoria] = 0;
      }
      stats.categories[gasto.categoria] += gasto.monto;
    });
    
    return Array.from(providerMap.values());
  }, [gastos]);
  
  // Filter providers by search text
  const filteredProviders = useMemo(() => {
    let filtered = [...providersStats];
    
    // Apply search filter
    if (searchText) {
      filtered = filtered.filter(provider => 
        provider.name.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      if (sortBy === 'name') {
        return sortDirection === 'asc' 
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else {
        return sortDirection === 'asc'
          ? a.totalSpent - b.totalSpent
          : b.totalSpent - a.totalSpent;
      }
    });
    
    return filtered;
  }, [providersStats, searchText, sortBy, sortDirection]);
  
  // Toggle sort direction
  const toggleSort = (field: 'name' | 'amount') => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('desc');
    }
  };
  
  // Get the primary category for a provider
  const getPrimaryCategory = (categories: Record<string, number>): [string, number] => {
    const entries = Object.entries(categories);
    if (entries.length === 0) return ['', 0];
    
    return entries.reduce((max, current) => {
      return current[1] > max[1] ? current : max;
    }, ['', 0] as [string, number]);
  };
  
  // Get color for a category
  const getCategoryColor = (category: string): BadgeColor => {
    const colors: Record<string, BadgeColor> = {
      transporte: 'primary',
      suministros: 'info',
      comisiones: 'warning',
      servicios: 'success',
      equipos: 'info',
      alquiler: 'error',
      otros: 'light'
    };
    return colors[category] || 'light';
  };
  
  // Format date
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (error) {
      return dateString || '—';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Gestionar proveedores"
      size="xl"
    >
      <div className="space-y-4 sm:space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          <div className="rounded-lg bg-white dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-blue-100 dark:bg-blue-900/30 p-2 sm:p-3 rounded-full">
                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">Total proveedores</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">{providersStats.length}</p>
              </div>
            </div>
          </div>
          
          <div className="rounded-lg bg-white dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-green-100 dark:bg-green-900/30 p-2 sm:p-3 rounded-full">
                <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">Gasto total</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(providersStats.reduce((sum, provider) => sum + provider.totalSpent, 0))}
                </p>
              </div>
            </div>
          </div>
          
          <div className="rounded-lg bg-white dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700 p-3 sm:p-4 sm:col-span-2 lg:col-span-1">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-purple-100 dark:bg-purple-900/30 p-2 sm:p-3 rounded-full">
                <BarChart className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">Promedio por proveedor</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(providersStats.length ? 
                    providersStats.reduce((sum, provider) => sum + provider.totalSpent, 0) / providersStats.length : 0
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Search and Filters */}
        <div className="space-y-2">
          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
            <Search className="w-3 h-3 sm:w-4 sm:h-4 inline-block mr-1.5 text-primary-500" />
            Buscar proveedor
          </label>
          <div className="relative">
            <Input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Nombre del proveedor..."
              className="w-full text-sm"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Se muestran {filteredProviders.length} de {providersStats.length} proveedores
            </p>
          </div>
        </div>
        
        {/* Sort options */}
        <div className="flex flex-wrap gap-2">
          <Button 
            variant={sortBy === 'name' ? 'primary' : 'outline'} 
            size="sm"
            onClick={() => toggleSort('name')}
            className="text-xs flex-1 sm:flex-none justify-center"
            startIcon={<ArrowUpDown className="w-3 h-3 mr-1" />}
          >
            Ordenar por nombre {sortBy === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
          </Button>
          <Button 
            variant={sortBy === 'amount' ? 'primary' : 'outline'} 
            size="sm"
            onClick={() => toggleSort('amount')}
            className="text-xs flex-1 sm:flex-none justify-center"
            startIcon={<ArrowUpDown className="w-3 h-3 mr-1" />}
          >
            Ordenar por monto {sortBy === 'amount' && (sortDirection === 'asc' ? '↑' : '↓')}
          </Button>
        </div>
        
        {/* Providers List */}
        <div className="border rounded-lg border-gray-200 dark:border-gray-700 overflow-hidden">
          {filteredProviders.length === 0 ? (
            <div className="text-center py-6 sm:py-8 bg-white dark:bg-gray-800/20">
              <div className="mx-auto flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-gray-100 dark:bg-gray-700">
                <Truck className="h-5 w-5 sm:h-6 sm:w-6 text-gray-500 dark:text-gray-400" />
              </div>
              <h3 className="mt-2 text-xs sm:text-sm font-medium text-gray-900 dark:text-white">No hay proveedores</h3>
              <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                No se encontraron proveedores con los filtros actuales.
              </p>
            </div>
          ) : (
            <div className="overflow-y-auto max-h-[350px] sm:max-h-[400px]">
              {/* List Header */}
              <div className="sticky top-0 z-10 flex items-center px-3 sm:px-4 py-2 sm:py-3 text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <div className="w-2/5">Proveedor</div>
                <div className="w-1/5 text-right">Monto</div>
                <div className="w-1/5 text-center hidden sm:block">Transacciones</div>
                <div className="w-1/5 text-right hidden sm:block">Última Transacción</div>
              </div>
            
              {/* List Items */}
              {filteredProviders.map((provider, index) => {
                const [primaryCategory] = getPrimaryCategory(provider.categories);
                
                return (
                  <div 
                    key={provider.name}
                    className={`
                      border-b border-gray-200 dark:border-gray-700 last:border-b-0
                      ${index % 2 === 0 ? 'bg-white dark:bg-gray-800/30' : 'bg-gray-50 dark:bg-gray-800/10'}
                    `}
                  >
                    {/* Main Provider Info */}
                    <div className="flex flex-wrap items-center px-3 sm:px-4 py-2 sm:py-3">
                      <div className="w-2/5 flex items-center min-w-0">
                        <div className="flex-shrink-0">
                          <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                            <User className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600 dark:text-gray-300" />
                          </div>
                        </div>
                        <div className="ml-3 sm:ml-4 min-w-0">
                          <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white truncate">
                            {provider.name}
                          </div>
                          <div className="text-xs mt-1">
                            {primaryCategory && (
                              <Badge variant="light" color={getCategoryColor(primaryCategory)}>
                                {primaryCategory.charAt(0).toUpperCase() + primaryCategory.slice(1)}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="w-1/5 text-right text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                        {formatCurrency(provider.totalSpent)}
                      </div>
                      <div className="w-1/5 text-center hidden sm:block text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                        {provider.transactions} {provider.transactions === 1 ? 'gasto' : 'gastos'}
                      </div>
                      <div className="w-1/5 text-right hidden sm:block text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                        {formatDate(provider.lastTransaction)}
                      </div>
                      {/* Mobile-only row for transactions and date */}
                      <div className="w-full mt-2 flex justify-between sm:hidden text-xs text-gray-500">
                        <span>{provider.transactions} {provider.transactions === 1 ? 'gasto' : 'gastos'}</span>
                        <span>{formatDate(provider.lastTransaction)}</span>
                      </div>
                    </div>
                    
                    {/* Categories Breakdown */}
                    <div className="bg-gray-50 dark:bg-gray-800/40 px-3 sm:px-4 py-2 sm:py-3 border-t border-gray-100 dark:border-gray-700/50">
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                        <FileText className="w-3 h-3 inline-block mr-1.5 text-primary-500" />
                        Desglose por categorías
                      </div>
                      <div className="flex flex-wrap gap-1.5 sm:gap-2">
                        {Object.entries(provider.categories).map(([category, amount]) => (
                          <div key={category} className="flex items-center mb-1">
                            <Badge variant="light" color={getCategoryColor(category)}>
                              {category.charAt(0).toUpperCase() + category.slice(1)}
                            </Badge>
                            <span className="text-xs ml-1 text-gray-700 dark:text-gray-300">
                              {formatCurrency(amount)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ProveedoresModal; 